import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  ArrowRightLeft,
  Bot,
  CheckCircle2,
  Download,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  Paperclip,
  Phone,
  X,
} from "lucide-react";
import { toast } from "sonner";
import ExcelViewer from "@/modern/components/excel-viewer";
import FilePreview from "@/modern/components/file-preview";
import PdfViewer from "@/modern/components/pdf-viewer";
import WordViewer from "@/modern/components/word-viewer";
import { Button } from "@/modern/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { LightboxImageTrigger, LightboxProvider } from "@/modern/components/ui/lightbox";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { getFileExtension } from "@/modern/utils";
import { downloadFileFromData } from "@/other/utils/utils.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { formatLocationLines } from "@/react/components/entityViews/location/utils/locationDisplay";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useLeadsContext } from "../context/LeadsContext";
import { useConvertLead } from "../hooks/useConvertLead";
import type { LinkedSubmissionFile } from "../hooks/useSubmissionFiles";
import { useSubmissionFiles } from "../hooks/useSubmissionFiles";
import { sourceLabel } from "../utils/sourceLabel";
import { ConvertLeadDialog } from "./ConvertLeadDialog";
import {
  DetailField,
  LeadAvatar,
  PanelSection,
  submissionFileToSwivuFile,
} from "./shared/leadsUi";

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"]);

const TEXT_EXTENSIONS = new Set([
  "txt",
  "md",
  "json",
  "xml",
  "csv",
  "js",
  "jsx",
  "ts",
  "tsx",
  "html",
  "css",
  "scss",
  "py",
  "java",
  "c",
  "cpp",
  "h",
  "cs",
  "php",
  "rb",
  "go",
  "rs",
  "swift",
  "kt",
  "rtf",
]);

const VIDEO_EXTENSIONS = new Set(["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"]);

const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"]);

const TEXT_PREVIEW_MAX_BYTES = 100_000;

type AttachmentKind = "image" | "pdf" | "text" | "docx" | "xlsx" | "video" | "audio" | "other";

type PreviewState =
  | { kind: "pdf"; url: string; name: string }
  | { kind: "text"; url: string; name: string }
  | { kind: "docx"; url: string; name: string }
  | { kind: "xlsx"; url: string; name: string }
  | { kind: "media"; url: string; name: string; file: SwivuFile }
  | null;

const isPagedDocumentPreview = (kind: NonNullable<PreviewState>["kind"]) =>
  kind === "pdf" || kind === "docx" || kind === "xlsx";

function getAttachmentKind(fileName: string): AttachmentKind {
  const ext = getFileExtension(fileName);
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (ext === "docx") return "docx";
  if (ext === "xlsx" || ext === "xls") return "xlsx";
  if (TEXT_EXTENSIONS.has(ext)) return "text";
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  return "other";
}

function attachmentLabel(attachment: LinkedSubmissionFile): string {
  return attachment.fileName ?? `File #${attachment.submissionFile.id}`;
}

function attachmentFileUrl(attachment: LinkedSubmissionFile): string {
  return attachment.submissionFile.url?.trim() || "";
}

function attachmentThumbUrl(attachment: LinkedSubmissionFile): string {
  return (
    attachment.submissionFile.thumbnailUrl?.trim() ||
    attachment.submissionFile.url?.trim() ||
    ""
  );
}

async function handleDownload(fileUrl: string | null | undefined, fileName: string | null) {
  const url = fileUrl?.trim();
  if (!url) {
    toast.error("File unavailable.");
    return;
  }
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`Failed to download file (${res.status})`);
    const blob = await res.blob();
    downloadFileFromData(blob, fileName ?? "attachment");
  } catch {
    toast.error("Failed to download file.");
  }
}

function TextFilePreviewContent({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`);

        const blob = await res.blob();
        const truncated = blob.size > TEXT_PREVIEW_MAX_BYTES;
        const slice = truncated ? blob.slice(0, TEXT_PREVIEW_MAX_BYTES) : blob;
        const text = await slice.text();

        if (!cancelled) {
          setContent(truncated ? `${text}\n\n… (preview truncated)` : text);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load file.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (error) {
    return <p className="p-6 text-sm text-destructive">{error}</p>;
  }

  return (
    <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm leading-relaxed text-foreground">
      {content}
    </pre>
  );
}

function AttachmentPreviewDialog({
  preview,
  onClose,
}: {
  preview: PreviewState;
  onClose: () => void;
}) {
  return (
    <Dialog open={preview != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={
          preview && isPagedDocumentPreview(preview.kind)
            ? "z-[--swivu-bryntum-z-index] flex h-[80dvh] max-w-4xl flex-col gap-0 p-0"
            : preview?.kind === "media"
              ? "z-[--swivu-bryntum-z-index] flex max-h-[80dvh] max-w-3xl flex-col gap-0 p-0"
              : "z-[--swivu-bryntum-z-index] flex h-[80dvh] max-w-3xl flex-col gap-0 p-0"
        }
      >
        {preview?.kind === "pdf" ? (
          <>
            <DialogTitle className="sr-only">{preview.name}</DialogTitle>
            <DialogDescription className="sr-only">PDF attachment preview</DialogDescription>
            <PdfViewer fileUrl={preview.url} fileName={preview.name} className="min-h-0 flex-1" />
          </>
        ) : preview?.kind === "docx" ? (
          <>
            <DialogTitle className="sr-only">{preview.name}</DialogTitle>
            <DialogDescription className="sr-only">Document attachment preview</DialogDescription>
            <WordViewer fileUrl={preview.url} fileName={preview.name} className="min-h-0 flex-1" />
          </>
        ) : preview?.kind === "xlsx" ? (
          <>
            <DialogTitle className="sr-only">{preview.name}</DialogTitle>
            <DialogDescription className="sr-only">Spreadsheet attachment preview</DialogDescription>
            <ExcelViewer fileUrl={preview.url} fileName={preview.name} className="min-h-0 flex-1" />
          </>
        ) : preview?.kind === "text" ? (
          <>
            <DialogHeader className="shrink-0 border-b border-border px-4 py-3">
              <DialogTitle className="truncate text-sm font-medium">{preview.name}</DialogTitle>
              <DialogDescription className="sr-only">Text file preview</DialogDescription>
            </DialogHeader>
            <TextFilePreviewContent url={preview.url} />
          </>
        ) : preview?.kind === "media" ? (
          <>
            <DialogHeader className="shrink-0 border-b border-border px-4 py-3">
              <DialogTitle className="truncate text-sm font-medium">{preview.name}</DialogTitle>
              <DialogDescription className="sr-only">Media attachment preview</DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
              <FilePreview
                file={preview.file}
                src={preview.url}
                enableVideoAudioRendering
                className="max-h-full w-full"
                showName={false}
              />
            </div>
          </>
        ) : (
          <>
            <DialogTitle className="sr-only">Attachment preview</DialogTitle>
            <DialogDescription className="sr-only">Attachment preview</DialogDescription>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AttachmentGridPreview({
  kind,
  file,
  thumbUrl,
  fileUrl,
  label,
  lightboxIndex,
  onPreview,
}: {
  kind: AttachmentKind;
  file: SwivuFile;
  thumbUrl: string;
  fileUrl: string;
  label: string;
  lightboxIndex: number | undefined;
  onPreview: (preview: PreviewState) => void;
}) {
  const iconPreview = (
    <FilePreview
      file={file}
      src={thumbUrl || undefined}
      className="size-full object-cover"
      showName={false}
      openFileOnClick={false}
      disableLoadingState
      isThumbnail
      fileIconClassName="size-10"
      fileIconContainerClassName="size-full"
    />
  );

  if (kind === "image" && lightboxIndex != null) {
    return (
      <LightboxImageTrigger asChild index={lightboxIndex}>
        <button
          type="button"
          className="size-full cursor-pointer text-left"
          aria-label={`View ${label}`}
        >
          <FilePreview
            file={file}
            src={thumbUrl}
            className="size-full object-cover"
            showName={false}
            openFileOnClick={false}
            disableLoadingState
            isThumbnail
            fileIconClassName="size-10"
            fileIconContainerClassName="size-full"
          />
        </button>
      </LightboxImageTrigger>
    );
  }

  if (kind === "pdf" && fileUrl) {
    return (
      <button
        type="button"
        className="size-full cursor-pointer text-left"
        aria-label={`Preview ${label}`}
        onClick={() => onPreview({ kind: "pdf", url: fileUrl, name: label })}
      >
        <PdfViewer fileUrl={fileUrl} fileName={label} isThumbnail className="size-full" />
      </button>
    );
  }

  if (kind === "text" && fileUrl) {
    return (
      <button
        type="button"
        className="size-full cursor-pointer text-left"
        aria-label={`Preview ${label}`}
        onClick={() => onPreview({ kind: "text", url: fileUrl, name: label })}
      >
        {iconPreview}
      </button>
    );
  }

  if (kind === "docx" && fileUrl) {
    return (
      <button
        type="button"
        className="size-full cursor-pointer text-left"
        aria-label={`Preview ${label}`}
        onClick={() => onPreview({ kind: "docx", url: fileUrl, name: label })}
      >
        {iconPreview}
      </button>
    );
  }

  if (kind === "xlsx" && fileUrl) {
    return (
      <button
        type="button"
        className="size-full cursor-pointer text-left"
        aria-label={`Preview ${label}`}
        onClick={() => onPreview({ kind: "xlsx", url: fileUrl, name: label })}
      >
        <ExcelViewer fileUrl={fileUrl} fileName={label} isThumbnail className="size-full" />
      </button>
    );
  }

  if ((kind === "video" || kind === "audio") && fileUrl) {
    return (
      <button
        type="button"
        className="size-full cursor-pointer text-left"
        aria-label={`Play ${label}`}
        onClick={() => onPreview({ kind: "media", url: fileUrl, name: label, file })}
      >
        {iconPreview}
      </button>
    );
  }

  return (
    <button
      type="button"
      className="size-full cursor-pointer text-left"
      aria-label={fileUrl ? `Open ${label}` : label}
      onClick={() => {
        if (fileUrl) window.open(fileUrl, "_blank", "noopener,noreferrer");
      }}
    >
      {iconPreview}
    </button>
  );
}

function LeadAttachmentsGrid({ attachments }: { attachments: LinkedSubmissionFile[] }) {
  const [preview, setPreview] = useState<PreviewState>(null);

  const { lightboxImages, lightboxIndexById } = useMemo(() => {
    const images: { src: string; alt?: string; caption?: string }[] = [];
    const indexById = new Map<number, number>();

    attachments.forEach((attachment) => {
      const name = attachmentLabel(attachment);
      if (getAttachmentKind(name) !== "image") return;

      const src = attachmentFileUrl(attachment) || attachmentThumbUrl(attachment);
      if (!src) return;

      indexById.set(attachment.submissionFile.id, images.length);
      images.push({ src, alt: name, caption: name });
    });

    return { lightboxImages: images, lightboxIndexById: indexById };
  }, [attachments]);

  const grid = (
    <div className="grid grid-cols-2 gap-3">
      {attachments.map((attachment) => {
        const label = attachmentLabel(attachment);
        const kind = getAttachmentKind(label);
        const file = submissionFileToSwivuFile(attachment);
        const fileUrl = attachmentFileUrl(attachment);
        const thumbUrl = attachmentThumbUrl(attachment);
        const lightboxIndex = lightboxIndexById.get(attachment.submissionFile.id);

        return (
          <div
            key={attachment.submissionFile.id}
            className="group relative overflow-hidden rounded-lg border border-border/80 bg-background shadow-sm"
          >
            <div className="relative aspect-square w-full overflow-hidden bg-muted/30">
              <AttachmentGridPreview
                kind={kind}
                file={file}
                thumbUrl={thumbUrl}
                fileUrl={fileUrl}
                label={label}
                lightboxIndex={lightboxIndex}
                onPreview={setPreview}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                className="absolute right-2 top-2 z-10 size-7 opacity-0 shadow-md transition-opacity group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(attachment.submissionFile.url, attachment.fileName);
                }}
                aria-label={`Download ${label}`}
              >
                <Download className="size-3.5" />
              </Button>
            </div>
            <div className="border-t border-border/60 px-2.5 py-2">
              <p className="flex items-center gap-1.5 truncate text-xs font-medium text-foreground">
                <MessageSquare className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate">{label}</span>
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {lightboxImages.length > 0 ? (
        <LightboxProvider
          images={lightboxImages}
          showThumbnails={lightboxImages.length > 1}
          allowZoom
        >
          {grid}
        </LightboxProvider>
      ) : (
        grid
      )}

      <AttachmentPreviewDialog preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

export function LeadDetailPanel() {
  const { user } = useSwivuBryntumContext();
  const { selectedSubmission, clearSelectedSubmission } = useLeadsContext();
  const attachments = useSubmissionFiles(selectedSubmission?.id);
  const {
    previewOpen,
    preview,
    previewLoading,
    confirming,
    startConvert,
    confirmConvert,
    openTask,
    setPreviewOpen,
  } = useConvertLead();

  if (!selectedSubmission) return null;

  const canViewPreview = Boolean(user?.can?.(GRANTS.grant_six));
  const canConfirmConvert = Boolean(user?.can?.(GRANTS.grant_four));
  const isConverted = selectedSubmission.isConverted;
  const locationRecord = selectedSubmission.location;
  const locationLines = formatLocationLines(locationRecord);

  const received =
    selectedSubmission.createdAt && !Number.isNaN(new Date(selectedSubmission.createdAt).getTime())
      ? format(new Date(selectedSubmission.createdAt), "MMM d, yyyy · h:mm a")
      : "—";

  const displayName = selectedSubmission.name?.trim() || "Lead";
  const hasLocation =
    Boolean(locationLines.heading) ||
    Boolean(locationLines.subLine) ||
    selectedSubmission.locationId != null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
      <div className="shrink-0 border-b border-border/60 px-4 py-3">
        <div className="flex items-start gap-3">
          <LeadAvatar name={selectedSubmission.name} size="md" />

          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold leading-5 text-foreground">
                  {displayName}
                </h3>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs leading-4 text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    {selectedSubmission.source === "ai_intake" ? (
                      <Bot className="size-3 shrink-0 opacity-70" aria-hidden />
                    ) : (
                      <MessageCircle className="size-3 shrink-0 opacity-70" aria-hidden />
                    )}
                    {sourceLabel(selectedSubmission.source)}
                  </span>
                  <span className="text-border" aria-hidden>
                    ·
                  </span>
                  <span>{received}</span>
                  {isConverted ? (
                    <>
                      <span className="text-border" aria-hidden>
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <CheckCircle2 className="size-3" aria-hidden />
                        Converted
                      </span>
                    </>
                  ) : null}
                </p>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="-mr-1 -mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={clearSelectedSubmission}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>

            {(canViewPreview || isConverted) && (
              <div className="mt-2">
                {isConverted ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 px-2.5 text-xs"
                    disabled={previewLoading || confirming}
                    onClick={() => {
                      if (selectedSubmission.taskId != null) {
                        openTask(selectedSubmission.taskId);
                        return;
                      }
                      void startConvert(selectedSubmission);
                    }}
                  >
                    <ExternalLink className="size-3.5" aria-hidden />
                    Open task
                  </Button>
                ) : canConfirmConvert ? (
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 gap-1.5 px-2.5 text-xs"
                    disabled={previewLoading || confirming}
                    onClick={() => void startConvert(selectedSubmission)}
                  >
                    {previewLoading ? (
                      <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    ) : (
                      <ArrowRightLeft className="size-3.5" aria-hidden />
                    )}
                    Convert to task
                  </Button>
                ) : (
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    You can view this lead, but you need edit access to convert it to a task.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <PanelSection label="Contact">
          <div className="grid gap-4 sm:grid-cols-2">
            <DetailField label="Email" icon={<Mail className="size-3" aria-hidden />}>
              {selectedSubmission.email?.trim() ? (
                <a
                  href={`mailto:${selectedSubmission.email}`}
                  className="text-primary hover:underline"
                >
                  {selectedSubmission.email}
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>
            <DetailField label="Phone" icon={<Phone className="size-3" aria-hidden />}>
              {selectedSubmission.phone?.trim() ? (
                <a
                  href={`tel:${selectedSubmission.phone}`}
                  className="text-primary hover:underline"
                >
                  {selectedSubmission.phone}
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>
          </div>
        </PanelSection>

        <PanelSection label="Message">
          {selectedSubmission.message?.trim() ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {selectedSubmission.message}
            </p>
          ) : (
            <p className="text-sm italic text-muted-foreground">No message provided</p>
          )}
        </PanelSection>

        {selectedSubmission.additionalNotes?.trim() && (
          <PanelSection label="Notes">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {selectedSubmission.additionalNotes}
            </p>
          </PanelSection>
        )}

        {hasLocation && (
          <PanelSection label="Location">
            <DetailField label="Address" icon={<MapPin className="size-3" aria-hidden />}>
              {locationLines.heading || locationLines.subLine ? (
                <div className="space-y-0.5">
                  {locationLines.heading ? (
                    <p className="text-foreground">{locationLines.heading}</p>
                  ) : null}
                  {locationLines.subLine ? (
                    <p className="text-muted-foreground">{locationLines.subLine}</p>
                  ) : null}
                </div>
              ) : (
                <span className="text-muted-foreground">Location linked (details unavailable)</span>
              )}
            </DetailField>
          </PanelSection>
        )}

        <PanelSection
          label="Attachments"
          hint={
            attachments.length > 0
              ? `${attachments.length} file${attachments.length === 1 ? "" : "s"}`
              : undefined
          }
        >
          {attachments.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
              <div className="flex size-10 items-center justify-center rounded-full border border-dashed border-border bg-muted/30 text-muted-foreground">
                <Paperclip className="size-4" aria-hidden />
              </div>
              <p className="text-sm text-muted-foreground">No attachments</p>
            </div>
          ) : (
            <LeadAttachmentsGrid attachments={attachments} />
          )}
        </PanelSection>
      </div>

      <ConvertLeadDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        preview={preview}
        loading={previewLoading}
        confirming={confirming}
        leadName={displayName}
        canConfirm={canConfirmConvert}
        onConfirm={() => {
          void confirmConvert(selectedSubmission);
        }}
      />
    </div>
  );
}
